import { inject } from '@angular/core';
import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, switchMap, take, tap, throwError, timeout } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthApiActions, AuthActions } from '../store/auth/auth.actions';
import { selectToken } from '../store/auth/auth.selectors';
import { AuthenticationService } from '../services/authentication.service';
import { SessionService } from '../services/session.service';
import { NotificationService } from '../services/notification.service';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { NavigationReturnService } from '../services/navigation-return.service';

/**
 * jwtInterceptor
 *
 * Interceptor funcional que:
 * 1. AdBoard el header `Authorization: Bearer <token>` a las peticiones
 *    dirigidas a la API del proyecto (environment.api_url).
 * 2. Ante un HTTP 401, intenta renovar el token via refreshToken().
 *    - Si el refresh es exitoso, reintenta la peticion original.
 *    - Si el refresh falla, ejecuta logout() y propaga el error.
 *
 * Las peticiones al endpoint de refresh no llevan el header de autorizacion
 * para evitar bucles infinitos.
 */
export const jwtInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const authService = inject(AuthenticationService);
  const actions$ = inject(Actions);
  const store = inject(Store);
  const sessionService = inject(SessionService);
  const notificationService = inject(NotificationService);
  const router = inject(Router);
  const navigationReturnService = inject(NavigationReturnService);

  const isApiRequest =
    req.url.startsWith(environment.apiUrl) || req.url.startsWith('/api/');
  const isRefreshRequest = req.url.includes('/auth/refreshToken');
  // Anonymous-by-design: a 401 here means an invalid current/temporary password,
  // not an expired session, so it must not trigger the refresh-token/logout flow.
  const isChangePasswordRequest = req.url.toLowerCase().includes('/auth/change-password');
  const isRecoveryRequest = /\/auth\/(?:forgot-password|reset-password)(?:[?#]|$)/i.test(req.url);

  const addAuthHeader = (
    request: HttpRequest<unknown>,
    token: string
  ): HttpRequest<unknown> =>
    request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

  const token = store.selectSignal(selectToken)();
  const authReq =
    isApiRequest && !isRefreshRequest && !isRecoveryRequest && token
      ? addAuthHeader(req, token)
      : req;

  return next(authReq).pipe(
    tap(() => sessionService.resetTimer()),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && isApiRequest && !isRefreshRequest && !isChangePasswordRequest && !isRecoveryRequest) {
        const refreshResult$ = actions$.pipe(
          ofType(AuthApiActions.refreshSucceeded, AuthApiActions.refreshFailed),
          take(1),
          timeout({ first: 10000 }),
          switchMap((result) => {
            if (result.type === AuthApiActions.refreshSucceeded.type) {
              return next(addAuthHeader(req, result.token));
            }
            store.dispatch(AuthActions.logoutRequested());
            return throwError(() => error);
          }),
          catchError(() => {
            store.dispatch(AuthActions.logoutRequested());
            return throwError(() => error);
          }),
        );
        return refreshResult$.pipe(
          tap({
            subscribe: () => queueMicrotask(() => store.dispatch(AuthActions.refreshRequested())),
          }),
        );
      }
      if (error.status === 403) {
        notificationService.error('AUTH.FORBIDDEN');
        router.navigateByUrl(navigationReturnService.getAuthorizedDestination(router));
      }
      return throwError(() => error);
    })
  );
};
